import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import type { AppDispatch, RootState } from '../../store';
import { closeChat, closeChatSession } from '../../store/chatBotSlice';
import { BotMessageSquare } from 'lucide-react';

interface ChatHeaderProps {
  isConnected: boolean;
  connectionStatus: 'connecting' | 'connected' | 'disconnected' | 'error';
}

export const ChatHeader: React.FC<ChatHeaderProps> = ({
  isConnected,
  connectionStatus
}) => {
  const dispatch = useDispatch<AppDispatch>();
  const { metrics, sessionId } = useSelector((state: RootState) => state.chatBot);

  const handleClose = () => {
    // Close the chat UI immediately for instant feedback
    dispatch(closeChat());

    // Close the session via API in the background (no await - don't block UI)
    if (sessionId) {
      dispatch(closeChatSession(sessionId)).catch((error) => {
        console.error('Failed to close session in background:', error);
        // Session will be cleaned up on next initialization or timeout
      });
    }
  };

  const getConnectionStatusText = () => {
    switch (connectionStatus) {
      case 'connecting':
        return 'Connecting...';
      case 'connected':
        return 'Online';
      case 'disconnected':
        return 'Offline';
      case 'error':
        return 'Connection Error';
      default:
        return 'Unknown';
    }
  };


  return (
    <div
      className="chat-header"
      style={{
        background: 'linear-gradient(180deg, #FFFFFF 0%, #F9FDFF 50%, #F5FBFE 100%)',
        color: '#1e293b',
        padding: '14px 20px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        borderBottom: '2px solid #D9F0FF',
        position: 'relative',
        transition: 'all 0.3s ease',
        cursor: 'default',
        borderRadius: '0',
        boxShadow: '0 2px 8px rgba(33, 155, 206, 0.08), 0 1px 4px rgba(0, 0, 0, 0.04)',
        backdropFilter: 'blur(10px)',
        minHeight: '64px',
        maxHeight: '64px'
      }}
    >
      {/* Left Section - Bot Info */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        gap: '8px'
      }}>
        {/* Bot Avatar - Bot Message Square Icon */}
        <div style={{
          width: '44px',
          height: '44px',
          backgroundColor: '#ffffff',
          borderRadius: '50%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          position: 'relative',
          overflow: 'hidden',
          border: '3px solid #D9F0FF',
          boxShadow: '0 2px 8px rgba(217, 240, 255, 0.4), 0 1px 4px rgba(0, 0, 0, 0.06)',
          transition: 'all 0.3s ease'
        }}>
          <BotMessageSquare size={24} color="#0f3340" strokeWidth={2.5} />
        </div>

        {/* Bot Info */}
        <div style={{
          display: 'flex',
          flexDirection: 'column'
        }}>
          <h3 style={{
            fontWeight: '700',
            fontSize: '16px',
            margin: 0,
            lineHeight: '1.2',
            background: 'linear-gradient(135deg, #0F3340 0%, #1a4d5f 100%)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            backgroundClip: 'text',
            fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", "Inter", "SF Pro Display", Roboto, sans-serif',
            letterSpacing: '-0.5px'
          }}>Moneyone Support</h3>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            fontSize: '12px',
            opacity: 0.9,
            marginTop: '2px'
          }}>
            {/* Connection Status Indicator */}
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '4px'
            }}>
              <div style={{
                width: '8px',
                height: '8px',
                borderRadius: '50%',
                backgroundColor: connectionStatus === 'connected' ? '#4ade80' :
                  connectionStatus === 'connecting' ? '#facc15' : '#f87171',
                animation: connectionStatus === 'connecting' ? 'pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite' : 'none'
              }}></div>
              <span style={{
                color: connectionStatus === 'connected' ? '#16a34a' :
                  connectionStatus === 'connecting' ? '#d97706' : '#dc2626',
                fontWeight: '500'
              }}>
                {getConnectionStatusText()}
              </span>
            </div>
            
            {/* Response Time (if available) */}
            {metrics.avgResponseTime > 0 && isConnected && (
              <>
                <span style={{
                  color: 'white',
                  opacity: 0.5
                }}>•</span>
                <span style={{ opacity: 0.75 }}>
                  ~{Math.round(metrics.avgResponseTime / 1000)}s response
                </span>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Right Section - Controls */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        gap: '8px'
      }}>
        {/* Close Button - Modern & Simple Design */}
        <button
          onClick={handleClose}
          style={{
            width: '32px',
            height: '32px',
            borderRadius: '50%',
            backgroundColor: 'transparent',
            border: 'none',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'pointer',
            transition: 'all 0.2s ease',
            outline: 'none',
            color: '#64748b',
            padding: 0
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.backgroundColor = '#f1f5f9';
            e.currentTarget.style.color = '#0f172a';
            e.currentTarget.style.transform = 'scale(1.1)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.backgroundColor = 'transparent';
            e.currentTarget.style.color = '#64748b';
            e.currentTarget.style.transform = 'scale(1)';
          }}
          aria-label="Close chat"
          title="Close"
        >
          <svg style={{ width: '20px', height: '20px' }} fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>
    </div>
  );
};

export default ChatHeader;